import axios, { type AxiosResponse } from '@fs/zion-axios'
import { ROOTSTECH_SERVICE as SERVICE } from '../../serviceNames'
import { getLocaleConfig, JSON_CONFIG } from '../../helpers/requestConfig'

// DOCS: /service/rootstech/registration'

type RegistrationBody = Record<string, unknown>

export type RegistrationType =
  'IN_PERSON' | 'ONLINE' | 'EXPO_HALL' | 'DISCOVERY_DAY' | 'SUBSCRIPTION_ONLY' | 'EASY' | 'ARC'

export type EventExternalId = {
  sourceSystem: string
  sourceId: string
}

export type RegistrationEvent = {
  code: string
  endDate: string | null
  externalIds?: EventExternalId[]
}

export type EventRegistration = {
  emailAddress: string
  eventCode: string
  active: boolean
  attended: boolean | null
  type: RegistrationType | null
  passTypes: RegistrationType[] | null
  firstName: string | null
  lastName: string | null
  mobilePhoneNumber: string | null
  country: string | null
  state: string | null
  confirmationCode: string | null
  cisUserId: string | null
}

export type AttendeeCounts = {
  inPerson: number
  online: number
  total: number
}

// country and state come back as "(Unspecified)" or empty when the registration has none.
export type RegionAttendeeCounts = {
  country: string
  state: string
  attendeeCounts: AttendeeCounts
}

export type RegistrationSortOrder = 'EMAIL' | 'PHONE' | 'FIRST' | 'LAST'

export type GetEventRegistrationsParams = {
  offset?: number
  limit?: number
  orderBy?: RegistrationSortOrder
}

const BASE = `${SERVICE}/registration`

export const DUPLICATE_REGISTRATION_STATUS = 409

export const register = async (body: RegistrationBody): Promise<AxiosResponse> =>
  axios.post(`${BASE}/vendor/rootstech`, body, getLocaleConfig())

export const registerForExpoHall = async (body: RegistrationBody): Promise<AxiosResponse> =>
  axios.post(`${BASE}/vendor/rootstech/expohall`, body, getLocaleConfig())

export const registerForARC = async (body: RegistrationBody): Promise<AxiosResponse> =>
  axios.post(`${BASE}/subscription/rootstech`, body, getLocaleConfig())

export const registerForDiscoveryDay = async (body: RegistrationBody): Promise<AxiosResponse> =>
  axios.post(`${BASE}/vendor/rootstech/discovery-day`, body, getLocaleConfig())

export const subscribe = async (body: RegistrationBody): Promise<AxiosResponse> =>
  axios.post(`${BASE}/vendor/rootstech/subscribe`, body, getLocaleConfig())

export const easyRegister = async (body: RegistrationBody): Promise<AxiosResponse> =>
  axios.post(`${BASE}/vendor/rootstech/easy-registration`, body, getLocaleConfig())

export const getSubscriptions = async (): Promise<AxiosResponse> =>
  axios.get(`${BASE}/vendor/rootstech/CURRENT/subscriptions-status`, getLocaleConfig())

export const getRegistrationStatus = async (): Promise<AxiosResponse> =>
  axios.get(`${BASE}/attendee/CURRENT/status`, getLocaleConfig())

export const updateSubscriptions = async (body: RegistrationBody): Promise<AxiosResponse> =>
  axios.put(`${BASE}/vendor/rootstech/preferences`, body, getLocaleConfig())

export const getEvents = async (): Promise<AxiosResponse<RegistrationEvent[]>> =>
  axios.get(`${BASE}/events`, JSON_CONFIG)

export const getEventRegistrations = async (
  eventCode: string,
  params?: GetEventRegistrationsParams
): Promise<AxiosResponse<EventRegistration[]>> =>
  axios.get(`${BASE}/events/${encodeURIComponent(eventCode)}/attendees`, { ...JSON_CONFIG, params })

export const getEventAttendeeCountsByRegion = async (
  eventCode: string
): Promise<AxiosResponse<RegionAttendeeCounts[]>> =>
  axios.get(`${BASE}/events/${encodeURIComponent(eventCode)}/attendees/counts-by-region`, JSON_CONFIG)

export const getEventRegistration = async (
  eventCode: string,
  emailAddress: string
): Promise<AxiosResponse<EventRegistration>> =>
  axios.get(
    `${BASE}/events/${encodeURIComponent(eventCode)}/attendees/${encodeURIComponent(emailAddress)}`,
    JSON_CONFIG
  )

export const deleteEventRegistration = async (eventCode: string, emailAddress: string): Promise<AxiosResponse<void>> =>
  axios.delete(
    `${BASE}/events/${encodeURIComponent(eventCode)}/attendees/${encodeURIComponent(emailAddress)}`,
    JSON_CONFIG
  )
